/**
 * Model Spec Sync Module for UI-Model-1
 *
 * Handles synchronization between Model Spec (userProjectDataModels) changes
 * and the UI Model state. When fields, categories, connectors, or submodels
 * are modified in the Model tab, this module ensures the UI Model reflects
 * those changes appropriately.
 *
 * Scenarios:
 * - Field deleted from Model Spec: Remove from UI Model + notify
 * - Field name changed: Update name on canvas + notify
 * - Category/Connector deleted: Remove from UI Model + notify
 * - Submodel deleted: Mark container as orphaned + warning
 * - Submodel name changed: Update name + notify
 */

import {
  TUpsertISRProjectDataModelsInput,
  TSubmodel,
  TField,
  TProductCategory,
  TConnector,
} from '../../Pages/DefineModel/model.d';
import { UIModelState, ContainerAPI, ItemAPI } from '../types/state.types';
import { isFieldAPI, isCategoryAPI, isConnectorAPI, isItemAPI } from '../types/api.types';
import { containerHasDirectSubmodelItems } from '../utils/submodelCleanup';
import { getBaseId } from '../utils/idGenerator';

// ============================================================================
// Types
// ============================================================================

export interface SyncResult {
  /** Item IDs that were removed due to Model Spec deletions */
  removedItems: string[];
  /** Item IDs that were updated due to Model Spec changes */
  updatedItems: string[];
  /** Container IDs that became orphaned (submodel deleted) */
  orphanedContainers: string[];
  /** Updated container IDs (submodel name changed) */
  updatedContainers: string[];
  /** Summary messages for user notification */
  messages: string[];
}

interface ModelSpecLookup {
  submodels: Map<string, TSubmodel>;
  fields: Map<string, TField>;
  categories: Map<string, TProductCategory>;
  connectors: Map<string, TConnector>;
}

// ============================================================================
// Lookup Map Builders
// ============================================================================

/**
 * Build lookup maps from Model Spec for efficient ID-based lookups
 */
function buildModelSpecLookup(
  modelSpec: TUpsertISRProjectDataModelsInput | null
): ModelSpecLookup {
  const lookup: ModelSpecLookup = {
    submodels: new Map(),
    fields: new Map(),
    categories: new Map(),
    connectors: new Map(),
  };

  if (!modelSpec?.project) return lookup;

  const { dataModels, fields, productCategories, connectors } = modelSpec.project;

  // Index submodels and their nested components
  dataModels?.forEach((submodel) => {
    if (submodel.id) {
      lookup.submodels.set(submodel.id, submodel);
    }

    // Index fields within submodels
    submodel.data?.fields?.forEach((field) => {
      if (field.id) {
        lookup.fields.set(field.id, field);
      }
    });

    // Index categories within submodels
    submodel.data?.productCategories?.forEach((cat) => {
      if (cat.id) {
        lookup.categories.set(cat.id, cat);
      }
    });

    // Index connectors within submodels
    submodel.data?.connectors?.forEach((conn) => {
      if (conn.id) {
        lookup.connectors.set(conn.id, conn);
      }
    });
  });

  // Index root-level components
  fields?.forEach((field) => {
    if (field.id) {
      lookup.fields.set(field.id, field.data);
    }
  });

  productCategories?.forEach((cat) => {
    if (cat.id) {
      lookup.categories.set(cat.id, cat);
    }
  });

  connectors?.forEach((conn) => {
    if (conn.id) {
      lookup.connectors.set(conn.id, conn);
    }
  });

  return lookup;
}

// ============================================================================
// Container Processing
// ============================================================================

/**
 * Process containers recursively to find items that need updating/removal
 */
function processContainers(
  containers: ContainerAPI[],
  lookup: ModelSpecLookup,
  result: SyncResult
): ContainerAPI[] {
  return containers.map((container) => {
    const updatedContainer = { ...container };

    // Check if container's submodel still exists
    if (container.content?.submodel?.id) {
      const submodelId = container.content.submodel.id;
      const currentSubmodel = lookup.submodels.get(submodelId);

      if (!currentSubmodel) {
        // Submodel was deleted - mark container as orphaned
        result.orphanedContainers.push(container.id);
        result.messages.push(
          `Container "${container.name}" references deleted submodel`
        );

        // Update metadata to indicate orphaned state
        updatedContainer.metadata = {
          ...updatedContainer.metadata,
          type: updatedContainer.metadata?.type || 'container',
          isNested: updatedContainer.metadata?.isNested || false,
          isOrphaned: true,
          orphanedSubmodelName: 'name' in container.content.submodel
            ? (container.content.submodel as { name: string }).name
            : submodelId,
        };
      } else if (
        'name' in container.content.submodel &&
        container.content.submodel.name !== currentSubmodel.data.name
      ) {
        // Submodel name changed - update the reference
        result.updatedContainers.push(container.id);
        result.messages.push(
          `Submodel renamed: "${container.content.submodel.name}" -> "${currentSubmodel.data.name}"`
        );

        updatedContainer.content = {
          ...updatedContainer.content,
          submodel: {
            ...container.content.submodel,
            name: currentSubmodel.data.name,
          },
        };
      }
    }

    // Free style: keep the content.submodels list in sync too — apply spec
    // renames per entry and drop entries whose submodel was deleted.
    if (container.content?.submodels?.length) {
      let listChanged = false;
      const syncedList = container.content.submodels
        .filter((sm) => {
          const exists = lookup.submodels.has(sm.id);
          if (!exists) {
            listChanged = true;
            result.messages.push(
              `Submodel "${sm.name}" removed from container "${container.name}" (deleted from Model Spec)`
            );
          }
          return exists;
        })
        .map((sm) => {
          const specSubmodel = lookup.submodels.get(sm.id);
          if (specSubmodel && sm.name !== specSubmodel.data.name) {
            listChanged = true;
            return { ...sm, name: specSubmodel.data.name };
          }
          return sm;
        });
      if (listChanged) {
        if (!result.updatedContainers.includes(container.id)) {
          result.updatedContainers.push(container.id);
        }
        updatedContainer.content = {
          ...updatedContainer.content,
          submodels: syncedList.length ? syncedList : undefined,
        };
      }
    }

    // Process items in the container
    const filteredItems: ItemAPI[] = [];
    container.content?.items?.forEach((item) => {
      if (!isItemAPI(item)) {
        return; // Skip non-item entries
      }

      let shouldKeep = true;
      let updatedItem = { ...item };

      if (isFieldAPI(item)) {
        // Resolve via the base spec ID — a free-style duplicate carries an
        // instance-suffixed ID but is still the same spec field.
        const fieldDef = lookup.fields.get(getBaseId(item.id));
        if (!fieldDef) {
          // Field was deleted from Model Spec
          result.removedItems.push(item.id);
          result.messages.push(`Field "${item.name}" removed (deleted from Model Spec)`);
          shouldKeep = false;
        } else if (item.name !== fieldDef.name) {
          // Field name changed
          result.updatedItems.push(item.id);
          result.messages.push(`Field renamed: "${item.name}" -> "${fieldDef.name}"`);
          updatedItem = {
            ...item,
            name: fieldDef.name,
            dataConfiguration: fieldDef.dataConfiguration as ItemAPI['configuration'],
          };
        }
      }

      if (isCategoryAPI(item)) {
        const catDef = lookup.categories.get(getBaseId(item.id));
        if (!catDef) {
          // Category was deleted from Model Spec
          result.removedItems.push(item.id);
          result.messages.push(
            `Category "${item.name}" removed (deleted from Model Spec)`
          );
          shouldKeep = false;
        } else if (item.name !== catDef.data.name) {
          // Category name changed
          result.updatedItems.push(item.id);
          result.messages.push(
            `Category renamed: "${item.name}" -> "${catDef.data.name}"`
          );
          updatedItem = { ...item, name: catDef.data.name };
        }
      }

      if (isConnectorAPI(item)) {
        const connDef = lookup.connectors.get(getBaseId(item.id));
        if (!connDef) {
          // Connector was deleted from Model Spec
          result.removedItems.push(item.id);
          result.messages.push(
            `Connector "${item.name}" removed (deleted from Model Spec)`
          );
          shouldKeep = false;
        } else if (item.name !== connDef.data.name) {
          // Connector name changed
          result.updatedItems.push(item.id);
          result.messages.push(
            `Connector renamed: "${item.name}" -> "${connDef.data.name}"`
          );
          updatedItem = { ...item, name: connDef.data.name };
        }
      }

      // Widgets are always kept (not from Model Spec)
      if (shouldKeep) {
        filteredItems.push(updatedItem as ItemAPI);
      }
    });

    updatedContainer.content = {
      ...updatedContainer.content,
      items: filteredItems,
    };

    // Update usageInfo to reflect removed components
    if (updatedContainer.usageInfo?.usedComponents) {
      updatedContainer.usageInfo = {
        ...updatedContainer.usageInfo,
        usedComponents: updatedContainer.usageInfo.usedComponents.filter(
          (uc) => !result.removedItems.includes(uc.id)
        ),
      };
    }

    // Recursively process nested containers
    if (container.content?.nestedContainers?.length) {
      updatedContainer.content = {
        ...updatedContainer.content,
        nestedContainers: processContainers(
          container.content.nestedContainers,
          lookup,
          result
        ),
      };
    }

    // Clear submodel if no items remain that reference it after sync
    const submodelId = updatedContainer.content.submodel?.id;
    if (submodelId && result.removedItems.length > 0) {
      if (!containerHasDirectSubmodelItems(updatedContainer, submodelId)) {
        updatedContainer.content = {
          ...updatedContainer.content,
          submodel: undefined,
        };
        result.messages.push(
          `Submodel reference removed from container "${container.name}" (no remaining items)`
        );
      }
    }

    return updatedContainer;
  });
}

// ============================================================================
// Main Sync Function
// ============================================================================

/**
 * Synchronize UI Model state with Model Spec changes
 *
 * This function compares the current UI Model state against the new Model Spec
 * and returns:
 * 1. Updated state with removed/updated items
 * 2. Sync result with details about changes for user notification
 *
 * @param currentState - Current UI Model state
 * @param newModelSpec - Updated Model Spec from userProjectDataModels
 * @returns Object containing updated state and sync result
 */
export function syncUIModelWithModelSpec(
  currentState: UIModelState,
  newModelSpec: TUpsertISRProjectDataModelsInput | null
): { updatedState: UIModelState; syncResult: SyncResult } {
  const syncResult: SyncResult = {
    removedItems: [],
    updatedItems: [],
    orphanedContainers: [],
    updatedContainers: [],
    messages: [],
  };

  // If no model loaded, return unchanged state
  if (!currentState.model) {
    return { updatedState: currentState, syncResult };
  }

  // If no model spec, can't sync
  if (!newModelSpec) {
    return { updatedState: currentState, syncResult };
  }

  // Build lookup maps for efficient ID-based access
  const lookup = buildModelSpecLookup(newModelSpec);

  // Deep clone the model to avoid mutations
  const updatedModel = JSON.parse(JSON.stringify(currentState.model));

  // Process each canvas
  Object.keys(updatedModel.layout.canvas).forEach((canvasId) => {
    const canvas = updatedModel.layout.canvas[canvasId];
    if (canvas.containers) {
      canvas.containers = processContainers(canvas.containers, lookup, syncResult);
    }
  });

  // Only update state if there were changes
  const hasChanges =
    syncResult.removedItems.length > 0 ||
    syncResult.updatedItems.length > 0 ||
    syncResult.orphanedContainers.length > 0 ||
    syncResult.updatedContainers.length > 0;

  if (!hasChanges) {
    return { updatedState: currentState, syncResult };
  }

  return {
    updatedState: {
      ...currentState,
      model: updatedModel,
    },
    syncResult,
  };
}

/**
 * Check if sync is needed by comparing Model Spec timestamps or content
 *
 * @param currentModelSpec - Previous Model Spec
 * @param newModelSpec - New Model Spec
 * @returns true if sync should be performed
 */
export function shouldSync(
  currentModelSpec: TUpsertISRProjectDataModelsInput | null,
  newModelSpec: TUpsertISRProjectDataModelsInput | null
): boolean {
  // Always sync if we had no previous spec and now have one
  if (!currentModelSpec && newModelSpec) return true;

  // No sync needed if both are null
  if (!currentModelSpec || !newModelSpec) return false;

  // Compare by stringifying (simple but effective for detecting changes)
  return JSON.stringify(currentModelSpec) !== JSON.stringify(newModelSpec);
}

/**
 * Generate user-friendly notification messages from sync result
 */
export function generateSyncNotifications(syncResult: SyncResult): {
  info: string[];
  warnings: string[];
} {
  const info: string[] = [];
  const warnings: string[] = [];

  if (syncResult.removedItems.length > 0) {
    info.push(
      `${syncResult.removedItems.length} item(s) removed due to Model Spec changes`
    );
  }

  if (syncResult.updatedItems.length > 0) {
    info.push(
      `${syncResult.updatedItems.length} item(s) updated from Model Spec`
    );
  }

  if (syncResult.updatedContainers.length > 0) {
    info.push(
      `${syncResult.updatedContainers.length} container(s) updated (submodel renamed)`
    );
  }

  if (syncResult.orphanedContainers.length > 0) {
    warnings.push(
      `${syncResult.orphanedContainers.length} container(s) need attention - referenced submodel deleted`
    );
  }

  return { info, warnings };
}

export default {
  syncUIModelWithModelSpec,
  shouldSync,
  generateSyncNotifications,
};
